(function () {
  "use strict";

  function initCalculator(root) {
    if (!root || root.dataset.initialized === "true") {
      return;
    }

    root.dataset.initialized = "true";

    const locale =
      document.documentElement.lang || "en";

    const isFrench =
      locale.toLowerCase().startsWith("fr");

    const daySuffix =
      isFrench ? "/j" : "/day";

    const hourDayHeader =
      isFrench ? "h/j" : "h/day";

    const energyDayHeader =
      isFrench ? "Wh/j" : "Wh/day";

    const formatter = new Intl.NumberFormat(
      locale,
      {
        maximumFractionDigits: 1
      }
    );

    const t = {
      device:
        root.getAttribute("data-t-device") ||
        "Device",

      source:
        root.getAttribute("data-t-source") ||
        "Source",

      battery:
        root.getAttribute("data-t-battery") ||
        "Battery",

      remove:
        root.getAttribute("data-t-remove") ||
        "Remove",

      selfSufficient:
        root.getAttribute(
          "data-t-self-sufficient"
        ) ||
        "Energy self-sufficient",

      deficit:
        root.getAttribute("data-t-deficit") ||
        "Energy deficit",

      noConsumption:
        root.getAttribute(
          "data-t-no-consumption"
        ) ||
        "No consumption",

      hours:
        root.getAttribute("data-t-hours") ||
        "hours",

      days:
        root.getAttribute("data-t-days") ||
        "days",

      intermittent:
        root.getAttribute(
          "data-t-intermittent"
        ) ||
        "Intermittent devices",

      alwaysOn:
        root.getAttribute(
          "data-t-always-on"
        ) ||
        "Always-on devices",

      charging:
        root.getAttribute(
          "data-t-charging"
        ) ||
        "Charging sources",

      batteries:
        root.getAttribute(
          "data-t-batteries"
        ) ||
        "Battery bank",

      none:
        root.getAttribute("data-t-none") ||
        "None"
    };

    function toNumber(value) {
      const normalized = String(value || "")
        .trim()
        .replace(/\s/g, "")
        .replace(",", ".");

      const number = Number(normalized);

      return Number.isFinite(number) &&
        number > 0
        ? number
        : 0;
    }

    function formatNumber(value) {
      return formatter.format(value);
    }

    function formatWh(value) {
      if (Math.abs(value) >= 1000) {
        return (
          formatNumber(value / 1000) +
          " kWh"
        );
      }

      return formatNumber(value) + " Wh";
    }

    function escapeHtml(value) {
      return String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
    }

    function makeRemoveButton() {
      return `
        <button
          type="button"
          class="bw-energy-remove"
          data-remove-row
          aria-label="${escapeHtml(t.remove)}"
        >×</button>
      `;
    }

    function addRow(type) {
      const table = root.querySelector(
        `[data-table="${type}"] tbody`
      );

      if (!table) {
        return;
      }

      const row =
        document.createElement("tr");

      if (type === "always") {
        row.innerHTML = `
          <td>
            <input
              type="text"
              class="bw-energy-name"
              placeholder="${escapeHtml(t.device)}"
            >
          </td>

          <td>
            <input
              type="text"
              inputmode="decimal"
              class="bw-energy-power"
              value="0"
            >
          </td>

          <td>
            <strong class="bw-energy-row-total">
              0 Wh
            </strong>
          </td>

          <td class="bw-energy-table__action">
            ${makeRemoveButton()}
          </td>
        `;
      } else if (type === "battery") {
        const firstBatterySelect =
          root.querySelector(
            '[data-table="battery"] select'
          );

        const options =
          firstBatterySelect
            ? firstBatterySelect.innerHTML
            : `
              <option value="0.5">
                Lead / AGM
              </option>

              <option value="0.9">
                Lithium
              </option>
            `;

        row.innerHTML = `
          <td>
            <select
              class="bw-energy-battery-type"
            >
              ${options}
            </select>
          </td>

          <td>
            <input
              type="text"
              inputmode="decimal"
              class="bw-energy-volts"
              value="12"
            >
          </td>

          <td>
            <input
              type="text"
              inputmode="decimal"
              class="bw-energy-ah"
              value="0"
            >
          </td>

          <td>
            <strong class="bw-energy-row-total">
              0 Wh
            </strong>
          </td>

          <td class="bw-energy-table__action">
            ${makeRemoveButton()}
          </td>
        `;
      } else {
        const placeholder =
          type === "charging"
            ? t.source
            : t.device;

        row.innerHTML = `
          <td>
            <input
              type="text"
              class="bw-energy-name"
              placeholder="${escapeHtml(
                placeholder
              )}"
            >
          </td>

          <td>
            <input
              type="text"
              inputmode="decimal"
              class="bw-energy-power"
              value="0"
            >
          </td>

          <td>
            <input
              type="text"
              inputmode="decimal"
              class="bw-energy-hours"
              value="0"
            >
          </td>

          <td>
            <strong class="bw-energy-row-total">
              0 Wh
            </strong>
          </td>

          <td class="bw-energy-table__action">
            ${makeRemoveButton()}
          </td>
        `;
      }

      table.appendChild(row);

      calculate();
    }

    function calculate() {
      let intermittent = 0;
      let alwaysOn = 0;
      let generation = 0;
      let usableBattery = 0;

      root
        .querySelectorAll(
          '[data-table="intermittent"] tbody tr'
        )
        .forEach(function (row) {
          const power = toNumber(
            row.querySelector(
              ".bw-energy-power"
            )?.value
          );

          const hours = toNumber(
            row.querySelector(
              ".bw-energy-hours"
            )?.value
          );

          const total = power * hours;

          const output =
            row.querySelector(
              ".bw-energy-row-total"
            );

          if (output) {
            output.textContent =
              formatWh(total);
          }

          intermittent += total;
        });

      root
        .querySelectorAll(
          '[data-table="always"] tbody tr'
        )
        .forEach(function (row) {
          const power = toNumber(
            row.querySelector(
              ".bw-energy-power"
            )?.value
          );

          const total = power * 24;

          const output =
            row.querySelector(
              ".bw-energy-row-total"
            );

          if (output) {
            output.textContent =
              formatWh(total);
          }

          alwaysOn += total;
        });

      root
        .querySelectorAll(
          '[data-table="charging"] tbody tr'
        )
        .forEach(function (row) {
          const power = toNumber(
            row.querySelector(
              ".bw-energy-power"
            )?.value
          );

          const hours = toNumber(
            row.querySelector(
              ".bw-energy-hours"
            )?.value
          );

          const total = power * hours;

          const output =
            row.querySelector(
              ".bw-energy-row-total"
            );

          if (output) {
            output.textContent =
              formatWh(total);
          }

          generation += total;
        });

      root
        .querySelectorAll(
          '[data-table="battery"] tbody tr'
        )
        .forEach(function (row) {
          const factor = toNumber(
            row.querySelector(
              ".bw-energy-battery-type"
            )?.value
          );

          const volts = toNumber(
            row.querySelector(
              ".bw-energy-volts"
            )?.value
          );

          const ah = toNumber(
            row.querySelector(
              ".bw-energy-ah"
            )?.value
          );

          const total =
            volts * ah * factor;

          const output =
            row.querySelector(
              ".bw-energy-row-total"
            );

          if (output) {
            output.textContent =
              formatWh(total);
          }

          usableBattery += total;
        });

      const consumption =
        intermittent + alwaysOn;

      const balance =
        generation - consumption;

      const consumptionEl =
        root.querySelector(
          '[data-result="consumption"]'
        );

      const generationEl =
        root.querySelector(
          '[data-result="generation"]'
        );

      const batteryEl =
        root.querySelector(
          '[data-result="battery"]'
        );

      const balanceEl =
        root.querySelector(
          '[data-result="balance"]'
        );

      const autonomyEl =
        root.querySelector(
          '[data-result="autonomy"]'
        );

      const statusEl =
        root.querySelector(
          '[data-result="status"]'
        );

      if (
        !consumptionEl ||
        !generationEl ||
        !batteryEl ||
        !balanceEl ||
        !autonomyEl ||
        !statusEl
      ) {
        return {
          intermittent,
          alwaysOn,
          consumption,
          generation,
          usableBattery,
          balance,
          autonomyText: "—",
          statusText: t.noConsumption
        };
      }

      consumptionEl.textContent =
        formatWh(consumption) +
        daySuffix;

      generationEl.textContent =
        formatWh(generation) +
        daySuffix;

      batteryEl.textContent =
        formatWh(usableBattery);

      balanceEl.textContent =
        (balance > 0 ? "+" : "") +
        formatWh(balance) +
        daySuffix;

      balanceEl.classList.remove(
        "is-positive",
        "is-negative"
      );

      if (balance > 0) {
        balanceEl.classList.add(
          "is-positive"
        );
      }

      if (balance < 0) {
        balanceEl.classList.add(
          "is-negative"
        );
      }

      statusEl.classList.remove(
        "is-positive",
        "is-negative"
      );

      let autonomyText = "—";
      let statusText =
        t.noConsumption;

      if (consumption > 0) {
        const averageHourlyConsumption =
          consumption / 24;

        const autonomyHours =
          usableBattery > 0
            ? usableBattery /
              averageHourlyConsumption
            : 0;

        if (autonomyHours > 0) {
          const autonomyDays =
            autonomyHours / 24;

          autonomyText =
            formatNumber(autonomyHours) +
            " " +
            t.hours;

          if (autonomyDays >= 1) {
            autonomyText +=
              " (" +
              formatNumber(autonomyDays) +
              " " +
              t.days +
              ")";
          }
        } else {
          autonomyText =
            "0 " + t.hours;
        }

        if (balance >= 0) {
          statusText =
            t.selfSufficient;

          statusEl.classList.add(
            "is-positive"
          );
        } else {
          statusText =
            t.deficit;

          statusEl.classList.add(
            "is-negative"
          );
        }
      }

      autonomyEl.textContent =
        autonomyText;

      statusEl.textContent =
        statusText;

      return {
        intermittent,
        alwaysOn,
        consumption,
        generation,
        usableBattery,
        balance,
        autonomyText,
        statusText
      };
    }

    function getName(row, fallback) {
      const value =
        row.querySelector(
          ".bw-energy-name"
        )?.value;

      return value && value.trim()
        ? value.trim()
        : fallback;
    }

    function makeReportTable(
      title,
      headers,
      rows
    ) {
      let html = "<section>";

      html +=
        "<h2>" +
        escapeHtml(title) +
        "</h2>";

      if (!rows.length) {
        html +=
          "<p>" +
          escapeHtml(t.none) +
          "</p>";

        html += "</section>";

        return html;
      }

      html += "<table>";
      html += "<thead><tr>";

      headers.forEach(function (header) {
        html +=
          "<th>" +
          escapeHtml(header) +
          "</th>";
      });

      html += "</tr></thead><tbody>";

      rows.forEach(function (row) {
        html += "<tr>";

        row.forEach(function (cell) {
          html +=
            "<td>" +
            escapeHtml(cell) +
            "</td>";
        });

        html += "</tr>";
      });

      html +=
        "</tbody></table></section>";

      return html;
    }

    function buildReport() {
      const results = calculate();

      const report =
        root.querySelector(
          "[data-energy-report]"
        );

      const reportContent =
        root.querySelector(
          "[data-report-content]"
        );

      const reportDate =
        root.querySelector(
          "[data-report-date]"
        );

      if (
        !report ||
        !reportContent ||
        !reportDate
      ) {
        return;
      }

      const intermittentRows = [];

      root
        .querySelectorAll(
          '[data-table="intermittent"] tbody tr'
        )
        .forEach(function (
          row,
          index
        ) {
          const power = toNumber(
            row.querySelector(
              ".bw-energy-power"
            )?.value
          );

          const hours = toNumber(
            row.querySelector(
              ".bw-energy-hours"
            )?.value
          );

          if (!power && !hours) {
            return;
          }

          intermittentRows.push([
            getName(
              row,
              t.device +
                " " +
                (index + 1)
            ),
            formatNumber(power) + " W",
            formatNumber(hours),
            formatWh(power * hours)
          ]);
        });

      const alwaysRows = [];

      root
        .querySelectorAll(
          '[data-table="always"] tbody tr'
        )
        .forEach(function (
          row,
          index
        ) {
          const power = toNumber(
            row.querySelector(
              ".bw-energy-power"
            )?.value
          );

          if (!power) {
            return;
          }

          alwaysRows.push([
            getName(
              row,
              t.device +
                " " +
                (index + 1)
            ),
            formatNumber(power) + " W",
            formatWh(power * 24)
          ]);
        });

      const chargingRows = [];

      root
        .querySelectorAll(
          '[data-table="charging"] tbody tr'
        )
        .forEach(function (
          row,
          index
        ) {
          const power = toNumber(
            row.querySelector(
              ".bw-energy-power"
            )?.value
          );

          const hours = toNumber(
            row.querySelector(
              ".bw-energy-hours"
            )?.value
          );

          if (!power && !hours) {
            return;
          }

          chargingRows.push([
            getName(
              row,
              t.source +
                " " +
                (index + 1)
            ),
            formatNumber(power) + " W",
            formatNumber(hours),
            formatWh(power * hours)
          ]);
        });

      const batteryRows = [];

      root
        .querySelectorAll(
          '[data-table="battery"] tbody tr'
        )
        .forEach(function (row) {
          const factor = toNumber(
            row.querySelector(
              ".bw-energy-battery-type"
            )?.value
          );

          const volts = toNumber(
            row.querySelector(
              ".bw-energy-volts"
            )?.value
          );

          const ah = toNumber(
            row.querySelector(
              ".bw-energy-ah"
            )?.value
          );

          if (!volts && !ah) {
            return;
          }

          const select =
            row.querySelector(
              ".bw-energy-battery-type"
            );

          const typeText =
            select?.options[
              select.selectedIndex
            ]?.text || t.battery;

          batteryRows.push([
            typeText,
            formatNumber(volts) + " V",
            formatNumber(ah) + " Ah",
            formatWh(
              volts * ah * factor
            )
          ]);
        });

      const headers = {
        device: [
          t.device,
          "W",
          hourDayHeader,
          energyDayHeader
        ],

        always: [
          t.device,
          "W",
          "Wh/24h"
        ],

        charge: [
          t.source,
          "W",
          hourDayHeader,
          energyDayHeader
        ],

        battery: [
          t.battery,
          "V",
          "Ah",
          "Wh"
        ]
      };

      reportContent.innerHTML =
        makeReportTable(
          t.intermittent,
          headers.device,
          intermittentRows
        ) +
        makeReportTable(
          t.alwaysOn,
          headers.always,
          alwaysRows
        ) +
        makeReportTable(
          t.charging,
          headers.charge,
          chargingRows
        ) +
        makeReportTable(
          t.batteries,
          headers.battery,
          batteryRows
        );

      reportDate.textContent =
        new Intl.DateTimeFormat(
          locale,
          {
            dateStyle: "long",
            timeStyle: "short"
          }
        ).format(new Date());

      const reportValues = {
        consumption:
          formatWh(
            results.consumption
          ) + daySuffix,

        generation:
          formatWh(
            results.generation
          ) + daySuffix,

        battery:
          formatWh(
            results.usableBattery
          ),

        balance:
          (
            results.balance > 0
              ? "+"
              : ""
          ) +
          formatWh(results.balance) +
          daySuffix,

        autonomy:
          results.autonomyText,

        status:
          results.statusText
      };

      Object.keys(
        reportValues
      ).forEach(function (key) {
        const element =
          root.querySelector(
            `[data-report-summary="${key}"]`
          );

        if (element) {
          element.textContent =
            reportValues[key];
        }
      });

      report.setAttribute(
        "aria-hidden",
        "false"
      );
    }

    root.addEventListener(
      "input",
      function () {
        calculate();
      }
    );

    root.addEventListener(
      "change",
      function () {
        calculate();
      }
    );

    root.addEventListener(
      "click",
      function (event) {
        const addButton =
          event.target.closest(
            "[data-add-row]"
          );

        if (addButton) {
          addRow(
            addButton.getAttribute(
              "data-add-row"
            )
          );

          return;
        }

        const removeButton =
          event.target.closest(
            "[data-remove-row]"
          );

        if (removeButton) {
          const row =
            removeButton.closest("tr");

          const tbody =
            row?.parentElement;

          if (!row || !tbody) {
            return;
          }

          if (
            tbody.children.length > 1
          ) {
            row.remove();
          } else {
            row
              .querySelectorAll("input")
              .forEach(function (input) {
                if (
                  input.classList.contains(
                    "bw-energy-name"
                  )
                ) {
                  input.value = "";
                } else if (
                  input.classList.contains(
                    "bw-energy-volts"
                  )
                ) {
                  input.value = "12";
                } else {
                  input.value = "0";
                }
              });
          }

          calculate();

          return;
        }

        const printButton =
          event.target.closest(
            "[data-print-report]"
          );

        if (printButton) {
          buildReport();

          document.body.classList.add(
            "bw-energy-printing"
          );

          window.print();
        }
      }
    );

    calculate();
  }

  function initAll(context) {
    const scope =
      context || document;

    scope
      .querySelectorAll(
        "[data-energy-calculator]"
      )
      .forEach(initCalculator);
  }

  if (
    document.readyState === "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      function () {
        initAll(document);
      }
    );
  } else {
    initAll(document);
  }

  document.addEventListener(
    "shopify:section:load",
    function (event) {
      initAll(event.target);
    }
  );

  window.addEventListener(
    "afterprint",
    function () {
      document.body.classList.remove(
        "bw-energy-printing"
      );
    }
  );
})();